import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Language selection dialog for downloads on non-English projects.
 *
 * Usage:
 *   <LanguageDownloadDialog
 *     open={showLang}
 *     projectLanguage="hindi"          // "english" | "hindi" | "malayalam" | ...
 *     onClose={() => setShowLang(false)}
 *     onSelect={(lang) => { setShowLang(false); doDownload(lang); }}
 *   />
 *
 *   // Parent's export handler:
 *   const onExport = () => {
 *     if (!projectLanguage || projectLanguage === "english" || projectLanguage === "en") {
 *       doDownload("english");  // silent English download
 *     } else {
 *       setShowLang(true);
 *     }
 *   };
 *
 * onSelect receives "original" | "english" which is the exact query-param value
 * to pass to the backend.
 */
const LANG_LABEL = {
  english: "English", en: "English",
  hindi: "हिन्दी (Hindi)", hi: "हिन्दी (Hindi)",
  malayalam: "മലയാളം (Malayalam)", ml: "മലയാളം (Malayalam)",
  tamil: "தமிழ் (Tamil)", ta: "தமிழ் (Tamil)",
  telugu: "తెలుగు (Telugu)", te: "తెలుగు (Telugu)",
  kannada: "ಕನ್ನಡ (Kannada)", kn: "ಕನ್ನಡ (Kannada)",
  mixed: "Original",
};

export const LanguageDownloadDialog = ({ open, onClose, projectLanguage, onSelect }) => {
  const label = LANG_LABEL[projectLanguage] || "Original";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent
        style={{ background: "var(--cb-surface)", borderColor: "var(--cb-border)", maxWidth: 420 }}
        data-testid="lang-download-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-lg font-bold tracking-tight"
            style={{ color: "var(--cb-text)" }}
            data-testid="lang-download-title"
          >
            Download language
          </DialogTitle>
          <DialogDescription style={{ color: "var(--cb-text-sec)" }} className="text-sm">
            This script is in <b>{label}</b>. Download the PDF in the original language,
            or in English translation?
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={() => onSelect && onSelect("original")}
            style={{ background: "transparent", borderColor: "var(--cb-border)", color: "var(--cb-text)" }}
            data-testid="lang-download-original-btn"
          >
            {label}
          </Button>
          <Button
            onClick={() => onSelect && onSelect("english")}
            className="font-semibold transition-opacity hover:opacity-90"
            style={{ background: "var(--cb-accent)", color: "#000" }}
            data-testid="lang-download-english-btn"
          >
            English
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Helper hook to determine if the project needs a language prompt. */
export const useProjectLanguage = (project) => {
  const lang = project?.breakdown_language
    || project?.script_language
    || project?.language
    || (project?.demo_key)   // demo_key is english|hindi|malayalam
    || "english";
  const isEnglish = !lang || lang === "english" || lang === "en" || lang === "en-us";
  return { language: lang, isEnglish };
};

export default LanguageDownloadDialog;
